import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { PreviewSectionId } from "./sections";

/**
 * « Sur cette page » — the desktop rail (Figma `540:23263`).
 *
 * **Desktop only, and that is a content decision rather than a space one.** At 360px the page is one
 * column and the sticky action bar already holds the only action that matters; adding an in-page
 * index above ~2 500px of content would push the round's identity below the fold on the screen
 * where it matters most. From `lg` the rail has a column of its own and earns its place.
 *
 * Plain anchors, not router links: these are same-document jumps, so the browser's own hash
 * behaviour — including the back button — is exactly right and needs no help. `scroll-mt-*` on the
 * sections keeps a jumped-to heading clear of the top bar.
 *
 * ## The active item is observed, not remembered
 *
 * The frame draws the current entry on a light ground, and the honest way to decide "current" on a
 * page this tall is to watch what is actually on screen. Highlighting whatever was last CLICKED
 * would go stale the moment someone scrolls — which on a page meant for reading is most of the
 * time — and would show nothing at all until the first click.
 */
export function PreviewNav({ sections }: { sections: PreviewSectionId[] }) {
  const { t } = useTranslation("acquire");
  const [active, setActive] = useState<PreviewSectionId | null>(
    sections[0] ?? null
  );

  useEffect(() => {
    /**
     * The active section is the LAST one whose top has crossed a line near the top of the viewport.
     *
     * ⚠️ This was an `IntersectionObserver` first and it was wrong twice, both found by scrolling
     * the real page:
     *
     * - **A short section is skipped entirely.** With the groups collapsed, "the complete dossier"
     *   is barely taller than the detection band, so it never became the first intersecting element
     *   and the rail jumped from the header straight to "what to bring".
     * - **The last section can never win.** Scrolled to the very bottom, a tall preceding section
     *   still intersects the band, so "what awaits you" stayed unreachable no matter how far down
     *   the page went.
     *
     * Comparing tops against a line has neither failure: exactly one section is "the last one above
     * the line" at any scroll position, including at the extremes. Five `getBoundingClientRect()`
     * calls per frame is nothing, and it is throttled to one per animation frame anyway.
     */
    let frame = 0;

    /** Sections that are actually on the page, in document order. */
    const present = () =>
      sections.filter(
        (section) => document.getElementById(`preview-${section}`) !== null
      );

    const measure = () => {
      frame = 0;
      const rendered = present();
      if (rendered.length === 0) return;

      /**
       * At the bottom of the page, the last section wins outright.
       *
       * Measured, not assumed: at 1440 the page bottoms out with the final section's top still at
       * +293px, below any sensible line — so "the last one above the line" can never select it, and
       * the rail would sit on the second-to-last entry while the reader looks at the last. Every
       * line position has this problem; the page is simply not tall enough to scroll the tail up.
       */
      const atBottom =
        window.scrollY + window.innerHeight >=
        document.documentElement.scrollHeight - 2;
      if (atBottom) {
        setActive(rendered[rendered.length - 1] ?? null);
        return;
      }

      // A quarter of the viewport down — below the sticky top bar, and high enough that a section
      // becomes current as it arrives rather than once it dominates the screen.
      const line = window.innerHeight * 0.25;
      let current: PreviewSectionId | null = null;

      for (const section of rendered) {
        const node = document.getElementById(`preview-${section}`);
        if (node && node.getBoundingClientRect().top <= line) current = section;
      }

      // Before the first section reaches the line (at the very top of the page) nothing is above it;
      // the first entry is the honest answer rather than no highlight at all.
      setActive(current ?? rendered[0] ?? null);
    };

    const onScroll = () => {
      if (frame === 0) frame = window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    // Section heights move with the viewport — and with a group being expanded, which changes the
    // page by ~800px without any scrolling at all.
    window.addEventListener("resize", onScroll, { passive: true });

    return () => {
      if (frame !== 0) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [sections]);

  return (
    <nav
      aria-label={t("preview.nav.label")}
      className="sticky top-lg flex flex-col gap-xxs"
    >
      <p className="px-sm pb-xs text-xs-semibold tracking-wide text-(--text-tertiary) uppercase">
        {t("preview.nav.label")}
      </p>
      {sections.map((section) => {
        const isActive = section === active;
        return (
          <a
            key={section}
            href={`#preview-${section}`}
            /**
             * `aria-current="true"` alongside the colour, so the highlight is not colour-only —
             * a screen reader announces which entry is current, and the contrast step is a
             * reinforcement rather than the whole signal (WCAG 1.4.1).
             */
            aria-current={isActive ? "true" : undefined}
            className={
              isActive
                ? "rounded-md bg-brand-50 px-sm py-2 text-sm-semibold text-brand-700 outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
                : "rounded-md px-sm py-2 text-sm-medium text-(--text-secondary) outline-none hover:bg-surface-page hover:text-(--text-primary) focus-visible:ring-2 focus-visible:ring-focus-ring"
            }
          >
            {t(`preview.nav.${section}`)}
          </a>
        );
      })}
    </nav>
  );
}
